import { apiFetch } from '../api-client';
import { getSessionToken } from '../auth-client';
import { mockPaymentsApi, isMockMode } from '../mock/payments-mock';

export type PaymentType = 'chi_thuong' | 'tam_ung' | 'ung_luong' | 'hoan_ung' | 'thuong_nong';
export type AdvanceType = 'planned' | 'urgent';
export type AdvancePurpose = 'thuong' | 'congtac';
export type CongTacVung = 'HN_HCM' | 'TinhI' | 'TinhKhac' | 'Foreign';
export type PaymentStatus =
  | 'DRAFT'
  | 'PENDING_NVKT'
  | 'PENDING_KTT'
  | 'PENDING_CEO'
  | 'APPROVED'
  | 'REJECTED'
  | 'DISBURSED'
  | 'CANCELLED';

export interface PaymentRequest {
  id: string;
  code: string;
  amount: number;
  type: PaymentType;
  advanceType?: AdvanceType | null;
  advancePurpose?: AdvancePurpose | null;
  ctLocation?: string | null;
  ctDays?: number | null;
  ctNhom?: number | null;
  ctVung?: CongTacVung | null;
  ctBudgetTotal?: number | null;
  urgency: boolean;
  status: PaymentStatus;
  reason: string;
  requesterId: string;
  requesterName?: string;
  department?: string | null;
  supplier?: string | null;
  bankAccount?: string | null;
  recipientBankName?: string | null;
  recipientBankBranch?: string | null;
  createdAt: string;
  submittedAt?: string | null;
  disbursedAt?: string | null;
  attachments?: Array<{ id: string; fileName: string; sizeBytes: number; mimeType: string }>;
  recipients?: Array<{ id?: string; employeeId: string; employeeName: string; amount: number; note?: string | null }>;
  cashVoucher?: {
    id: string;
    code: string;
    method: 'tien_mat_vnd' | 'chuyen_khoan' | 'tien_mat_usd';
    voucherDate: string;
    documentDate: string;
    bankAccountUsed: string | null;
    uncNumber: string | null;
    actualAmount: number;
    receiverType: 'employee' | 'supplier' | 'customer' | 'other';
    receiverName: string;
    receiverIdNumber: string | null;
    note: string | null;
    debitAccount: string;
    creditAccount: string;
    createdByName: string;
    createdAt: string;
  } | null;
  requester?: { id: string; name: string; code?: string; departmentName?: string };
  history?: Array<{
    id: string;
    actorName: string;
    actorRole: string;
    action: string;
    fromStatus?: string | null;
    toStatus?: string | null;
    note?: string | null;
    createdAt: string;
  }>;
}

export interface CreatePaymentInput {
  amount: number;
  type: PaymentType;
  advanceType?: AdvanceType;
  advancePurpose?: AdvancePurpose;
  ctLocation?: string;
  ctDays?: number;
  ctNhom?: number;
  ctVung?: CongTacVung;
  ctBudgetTotal?: number;
  urgency?: boolean;
  reason: string;
  supplier?: string;
  department?: string;
  bankAccount?: string;
  recipientBankName?: string;
  recipientBankBranch?: string;
  attachmentIds?: string[];
  /** BẮT BUỘC khi type=thuong_nong. Danh sách NV nhận thưởng + số tiền cá nhân. */
  recipients?: Array<{ employeeId: string; amount: number; note?: string }>;
}

export interface ListQuery {
  status?: PaymentStatus;
  type?: PaymentType;
  advanceType?: AdvanceType;
  advancePurpose?: AdvancePurpose;
  urgency?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface Paged<T> {
  items: T[];
  paging: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

function qs(query: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const paymentsApi = {
  async list(query: ListQuery = {}): Promise<Paged<PaymentRequest>> {
    if (isMockMode()) return mockPaymentsApi.list(query);
    return apiFetch(`/api/payment-requests${qs(query as Record<string, unknown>)}`);
  },
  async get(id: string): Promise<PaymentRequest> {
    if (isMockMode()) return mockPaymentsApi.get(id);
    return apiFetch(`/api/payment-requests/${id}`);
  },
  async advanceRequestPdf(id: string): Promise<Blob> {
    const token = getSessionToken();
    const response = await fetch(`/api/payment-requests/${id}/advance-request.pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) throw new Error(await response.text());
    return response.blob();
  },
  async create(input: CreatePaymentInput): Promise<PaymentRequest> {
    if (isMockMode()) return mockPaymentsApi.create(input);
    return apiFetch(`/api/payment-requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  async update(id: string, input: Partial<CreatePaymentInput>): Promise<PaymentRequest> {
    if (isMockMode()) return mockPaymentsApi.update(id, input);
    return apiFetch(`/api/payment-requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  async remove(id: string): Promise<{ ok: true }> {
    if (isMockMode()) return mockPaymentsApi.remove(id);
    return apiFetch(`/api/payment-requests/${id}`, { method: 'DELETE' });
  },
  async submit(id: string): Promise<PaymentRequest> {
    if (isMockMode()) return mockPaymentsApi.submit(id);
    return apiFetch(`/api/payment-requests/${id}/submit`, { method: 'POST' });
  },
  async disburse(id: string, proofAttachmentIds: string[]): Promise<PaymentRequest> {
    if (isMockMode()) return mockPaymentsApi.disburse(id);
    return apiFetch(`/api/payment-requests/${id}/disburse`, { method: 'POST', body: JSON.stringify({ proofAttachmentIds }) });
  },
  async cancel(id: string): Promise<PaymentRequest> {
    if (isMockMode()) return mockPaymentsApi.cancel(id);
    return apiFetch(`/api/payment-requests/${id}/cancel`, { method: 'POST' });
  },
};
